import { useEffect, useState } from "react";
import { Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const WAIT_MS = 10 * 60 * 1000;
const TONES = {
  ok: "border-emerald-200 bg-emerald-50 text-emerald-700", wait: "border-sky-200 bg-sky-50 text-sky-700",
  warn: "border-amber-200 bg-amber-50 text-amber-700", err: "border-rose-200 bg-rose-50 text-rose-700",
};

/** Status of a platform tag request + the WhatsApp automation that should follow it. */
export const welcomeStatus = (tagStatus, welcomeAt, tag) => {
  const st = (tagStatus || {})[tag];
  if (!st) return null;
  if (st.status === "pendiente") return { label: "Etiqueta pendiente", tone: "wait" };
  if (st.status === "error") return { label: `Error al etiquetar: ${st.error || "motivo desconocido"}`, tone: "err" };
  const at = new Date(st.at).getTime();
  if (welcomeAt && new Date(welcomeAt).getTime() >= at - 60000) return { label: `Bienvenida enviada ${fmtDateTime(welcomeAt)}`, tone: "ok" };
  if (Date.now() - at > WAIT_MS) return { label: "Sin envío de la plataforma", tone: "warn", hint: "Revisa que el Workflow esté publicado y la plantilla aprobada" };
  return { label: `Etiqueta enviada ${fmtDateTime(st.at)}`, tone: "wait" };
};

const Chip = ({ s, prefix, testid }) => (
  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${TONES[s.tone]}`} title={s.hint || ""} data-testid={testid}>
    {prefix}{s.label}{s.hint && <span className="ml-1 font-normal">· {s.hint}</span>}
  </span>
);

export const WelcomeChips = ({ tagStatus, welcomeAt, testid = "welcome-status" }) => {
  const web = welcomeStatus(tagStatus, welcomeAt, "formulario-web");
  const start = welcomeStatus(tagStatus, welcomeAt, "iniciar-whatsapp");
  if (!web && !start) return <span className="text-xs text-slate-400" data-testid={testid}>—</span>;
  return (
    <span className="inline-flex flex-wrap gap-1.5">
      {web && <Chip s={web} prefix="" testid={testid} />}
      {start && <Chip s={start} prefix="Inicio: " testid={`${testid}-start`} />}
    </span>
  );
};

export const StartWhatsAppButton = ({ leadId, name, phone, seed, onDone, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) setBusy(false); }, [open]);
  if (!phone || seed) return null;
  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/leads/${leadId}/whatsapp-start`);
      toast.success("Envío de bienvenida por WhatsApp solicitado");
      onDone?.();
    } catch (e) {
      const status = e.response?.status;
      if (status === 429) toast.warning(errorMessage(e));
      else toast.error(errorMessage(e), { action: status === 502 ? { label: "Reintentar", onClick: run } : undefined });
      onDone?.();
    } finally { setBusy(false); setOpen(false); }
  };
  return (
    <>
      <Button onClick={() => setOpen(true)} disabled={busy} className={`rounded-xl bg-emerald-600 hover:bg-emerald-700 ${className}`} data-testid="start-whatsapp-button">
        {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <MessageCircle className="mr-1.5 h-4 w-4" />} Iniciar WhatsApp
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="start-whatsapp-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Iniciar WhatsApp</AlertDialogTitle>
            <AlertDialogDescription>Se enviará la plantilla de bienvenida por WhatsApp a {name} ({phone})</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="start-whatsapp-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); run(); }} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="start-whatsapp-confirm">
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Enviar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
